import { Flex, NumberFormat, SkeletonBox, Spacing, Text } from '@billy/design-system'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { getCurrentLocale } from '../../../../../../utils'
import { useSalesReportStatistics } from '../../contexts/salesReportStatisticsContext'
import { SalesReportStatisticsVariant } from '../../types/salesReportStatisticsVariant'
import { SalesReportSummaryItem } from './elements/SalesReportSummaryItem'
import * as Styled from './styles'

const LOADING_SKELETON_HEIGHT = 40
const LOADING_SKELETON_WIDTH = 100

type SummaryTotalAmountTitle = Omit<Record<SalesReportStatisticsVariant, string>, 'overview'>

export const SalesReportSummary = (): ReactElement | null => {
  const { t } = useTranslation()
  const locale = getCurrentLocale()
  const { organization, isLoading: isUserOrganizationLoading } = useUserOrganization()
  const {
    graphSummary,
    isLoading: isSalesReportStatisticsLoading,
    shouldShowUnpaid,
    variant,
  } = useSalesReportStatistics()

  const summaryTotalAmountTitle: SummaryTotalAmountTitle = useMemo(
    () => ({
      'by-customer': t('sales_report.sales_by_customer.statistics.summary.total'),
      'by-item': t('sales_report.sales_by_item.statistics.summary.total'),
    }),
    [t],
  )

  if (!graphSummary || isUserOrganizationLoading || !organization?.baseCurrencyId) {
    return (
      <Styled.SalesReportSummaryWrapper>
        <SkeletonBox fullHeight fullWidth />
      </Styled.SalesReportSummaryWrapper>
    )
  }

  const { unpaid, paid, total } = graphSummary
  const formattedTotalCost = NumberFormat.getFormattedNumber(total, locale, { minimumFractionDigits: 2 })
  const summaryTotalAmountValue = graphSummary.totalAmount || 0

  return (
    <Styled.SalesReportSummaryWrapper>
      <Flex flexDirection="column" gap={Spacing.S16}>
        <Flex flexDirection="column">
          <Text uppercase variant="bodySM" colorVariant="secondary">
            {t('sales_report.statistics.summary.all_sales', { currency: organization.baseCurrencyId })}
          </Text>
          {isSalesReportStatisticsLoading ? (
            <SkeletonBox height={LOADING_SKELETON_HEIGHT} width={LOADING_SKELETON_WIDTH} />
          ) : (
            <Text title={formattedTotalCost} truncate uppercase variant="h2" weight="regular">
              {formattedTotalCost}
            </Text>
          )}
        </Flex>
        <SalesReportSummaryItem shouldFormatNumber isLoading={isSalesReportStatisticsLoading} value={paid}>
          {t('sales_report.statistics.graph.invoices.paid')}
        </SalesReportSummaryItem>
        {shouldShowUnpaid && (
          <SalesReportSummaryItem shouldFormatNumber isLoading={isSalesReportStatisticsLoading} value={unpaid}>
            {t('sales_report.statistics.graph.invoices.unpaid')}
          </SalesReportSummaryItem>
        )}
        {variant !== 'overview' && (
          <SalesReportSummaryItem isLoading={isSalesReportStatisticsLoading} value={summaryTotalAmountValue}>
            {summaryTotalAmountTitle[variant]}
          </SalesReportSummaryItem>
        )}
      </Flex>
    </Styled.SalesReportSummaryWrapper>
  )
}
